import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ApolloQueryResult } from '@apollo/client/core';
import { FILTER_BRANDS_QUERY, FILTER_SERIES_COUNT_QUERY } from '@shared/consts/graphql';
import { RESULT_PLURALS } from '@shared/consts/plurals';
import { Brand } from '@shared/graphql/generated';
import { BrandsGroup } from '@shared/models/brands-group';
import { FilterValue } from '@shared/models/filter-value';
import { SelectOption } from '@shared/models/select-option';
import { PluralPipe } from '@shared/pipes/plural.pipe';
import { FilterValueService } from '@shared/services/filter-value.service';
import { LoaderComponent } from '@shared/ui-components/loader/loader.component';
import { Apollo } from 'apollo-angular';
import { debounceTime, map, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'evland-catalog-filter',
  templateUrl: './filter.component.html',
  styleUrls: [ './filter.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    PluralPipe,
    MatIcon,
    ReactiveFormsModule,
    LoaderComponent,
  ],
})
export class FilterComponent implements OnInit, OnChanges, OnDestroy {
  initValue = input.required<FilterValue>();
  @Output() filterChange = new EventEmitter<FilterValue>();

  RESULT_PLURALS = RESULT_PLURALS;

  form: UntypedFormGroup;
  wheeldriveOptions: SelectOption[] = [
    { value: 'Full', label: $localize`Повний` },
    { value: 'Rear', label: $localize`Задній` },
    { value: 'Front', label: $localize`Передній` },
  ];
  groupedBrands: WritableSignal<BrandsGroup[]> = signal([]);
  totalResults: WritableSignal<number> = signal(0);
  showFilter: WritableSignal<boolean> = signal(false);
  isLoading: WritableSignal<boolean> = signal(true);
  isLoadingBrands: WritableSignal<boolean> = signal(true);

  get activeFiltersCount(): number {
    return Object.values(this.form.value).filter(v => !!v).length;
  }

  get isFormDirty(): boolean {
    return Object.values(this.form.getRawValue()).some(v => !!v);
  }

  private readonly destroyed$ = new Subject<void>();

  #apollo = inject(Apollo);
  #fb = inject(UntypedFormBuilder);
  #filterValueService = inject(FilterValueService);

  constructor(
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.initData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { initValue } = changes;

    if (!initValue || JSON.stringify(initValue.previousValue) === JSON.stringify(initValue.currentValue)) return;

    const {
      brand_id,
      wheeldrive,
      price_from,
      price_to,
      year_from,
      year_to,
      recharge_mileage_from,
      recharge_mileage_to,
      battery_capacity_from,
      battery_capacity_to,
    } = initValue.currentValue;

    this.form.reset(
      {
        brand_id: brand_id || '',
        wheeldrive: wheeldrive || '',
        price_from: price_from ? +price_from : null,
        price_to: price_to ? +price_to : null,
        year_from: year_from ? +year_from : null,
        year_to: year_to ? +year_to : null,
        recharge_mileage_from: recharge_mileage_from ? +recharge_mileage_from : null,
        recharge_mileage_to: recharge_mileage_to ? +recharge_mileage_to : null,
        battery_capacity_from: battery_capacity_from ? +battery_capacity_from : null,
        battery_capacity_to: battery_capacity_to ? +battery_capacity_to : null,
      },
      { emitEvent: !initValue.firstChange },
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onClearClick(): void {
    this.form.reset({ brand_id: '' });
    this.onSubmit(true);
  }

  onSubmit(force?: boolean): void {
    if (!force && (!this.totalResults() || this.form.pristine || this.isLoading())) return;

    const filterValue = this.form.getRawValue();

    this.filterChange.emit(filterValue);
    this.form.reset(filterValue, { emitEvent: false });

    if (window.innerWidth < 768) this.onToggleFilterClick();
  }

  onToggleFilterClick(): void {
    this.showFilter.set(!this.showFilter());

    this.document.body.style.overflow = this.showFilter() ? 'hidden' : 'initial';
  }

  onWindowResize(): void {
    if (!this.showFilter() || window.innerWidth < 768) return;

    this.showFilter.set(false);

    this.document.body.style.overflow = this.showFilter() ? 'hidden' : 'initial';
  }

  private initData(): void {
    this.#apollo.watchQuery({ query: FILTER_BRANDS_QUERY })
      .valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        map((result: ApolloQueryResult<{ brands: Brand[] }>) => result.data.brands.filter(item => !!item.series_count)),
      )
      .subscribe((brands: Brand[]) => {
        this.groupedBrands.set(brands.reduce((acc: BrandsGroup[], brand: Brand) => {
          const { letter: rawLetter, series_count, ...restBrand } = brand;
          const letter = rawLetter === '#' ? '0-9' : rawLetter;
          const foundIndex = acc.findIndex(group => group.letter === letter);

          if (foundIndex === -1) {
            return [
              ...acc,
              { letter, brands: [ brand ] }
            ];
          }

          return acc.map((group, index) => index === foundIndex ? {
            ...group,
            brands: [ ...group.brands, brand ],
          } : group);
        }, []).sort((a, b) => a.letter > b.letter ? 1 : -1));
        this.isLoadingBrands.set(false);
        this.form.get('brand_id').enable();
      });
  }

  private initForm(): void {
    this.form = this.#fb.group({
      brand_id: { value: '', disabled: true },
      wheeldrive: '',
      price_from: null,
      price_to: null,
      year_from: null,
      year_to: null,
      recharge_mileage_from: null,
      recharge_mileage_to: null,
      battery_capacity_from: null,
      battery_capacity_to: null,
    });

    this.form.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        startWith({}),
        debounceTime(100),
        map(() => this.form.getRawValue()),
        tap(() => this.isLoading.set(true)),
        map((filterValue: FilterValue) => this.#filterValueService.toApiValues(filterValue)),
        switchMap((filterValue: FilterValue) => this.#apollo.watchQuery({
          query: FILTER_SERIES_COUNT_QUERY,
          variables: filterValue
        }).valueChanges),
        map((result: ApolloQueryResult<{ countSeries: number }>) => result.data.countSeries),
      )
      .subscribe((countSeries: number) => {
        this.totalResults.set(countSeries);
        this.isLoading.set(false);
      });
  }
}
